import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { mockKeys, saveRoute } from "./mock-queries";
import {
  MOCK_ROUTE_METHODS,
  type MockRoute,
  type MockRouteMatchType,
  type MockRouteMethod,
} from "../../../shared/types";
import { Button } from "../../components/Button";
import { ErrorBanner } from "../../components/Feedback";
import { Modal } from "../../components/Modal";

type RouteFormModalProps = {
  /** Mock the route belongs to */
  tokenUuid: string;
  /** Route to edit; null adds a new one */
  route: MockRoute | null;
  /** Closes the dialog */
  onClose: () => void;
};

const CONTENT_TYPES = [
  "application/json",
  "text/plain",
  "text/html",
  "application/xml",
  "application/x-www-form-urlencoded",
];

const parseHeaders = (text: string): Record<string, string> => {
  if (!text.trim()) {
    return {};
  }
  const value: unknown = JSON.parse(text);
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(
      `Headers must be a JSON object, e.g. ${JSON.stringify({ "X-Request-Id": "abc" })}`,
    );
  }
  return Object.fromEntries(
    Object.entries(value).map(([name, header]) => [name, String(header)]),
  );
};

/** Add or edit one route of a mock. */
export const RouteFormModal = ({
  tokenUuid,
  route,
  onClose,
}: RouteFormModalProps) => {
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<unknown>(null);
  const save = useMutation({
    mutationFn: (body: Parameters<typeof saveRoute>[2]) =>
      saveRoute(tokenUuid, route?.uuid ?? null, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: mockKeys.detail(tokenUuid),
      });
      void queryClient.invalidateQueries({ queryKey: mockKeys.all });
      onClose();
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);
    const form = new FormData(event.currentTarget);
    let responseHeaders: Record<string, string>;
    try {
      responseHeaders = parseHeaders(String(form.get("responseHeaders")));
    } catch (error) {
      setFormError(
        error instanceof SyntaxError
          ? new Error("Headers are not valid JSON")
          : error,
      );
      return;
    }
    const body = String(form.get("responseContent"));
    save.mutate({
      requestMethod: String(form.get("requestMethod")) as MockRouteMethod,
      requestPath: String(form.get("requestPath")),
      matchType: String(form.get("matchType")) as MockRouteMatchType,
      responseStatus: Number(form.get("responseStatus")),
      responseType: String(form.get("responseType")) || null,
      responseContent: body === "" ? null : body,
      responseHeaders,
      delayMs: Number(form.get("delayMs") || 0),
      description: String(form.get("description")).trim() || null,
    });
  };

  return (
    <Modal title={route ? "Edit route" : "Add route"} onClose={onClose} wide>
      <form className="space-y-4" onSubmit={submit}>
        <ErrorBanner error={formError ?? save.error} />
        <div className="grid gap-4 sm:grid-cols-[8rem_1fr_9rem]">
          <div>
            <label className="label" htmlFor="requestMethod">
              Method
            </label>
            <select
              id="requestMethod"
              name="requestMethod"
              className="input"
              defaultValue={route?.requestMethod ?? "POST"}
            >
              {MOCK_ROUTE_METHODS.map((method) => (
                <option key={method} value={method}>
                  {method === "*" ? "Any" : method}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="requestPath">
              Path after the token
            </label>
            <input
              id="requestPath"
              name="requestPath"
              className="input font-mono"
              placeholder="/v1/members"
              defaultValue={route?.requestPath ?? ""}
              required
              maxLength={2048}
              autoFocus
            />
          </div>
          <div>
            <label className="label" htmlFor="matchType">
              Match
            </label>
            <select
              id="matchType"
              name="matchType"
              className="input"
              defaultValue={route?.matchType ?? "exact"}
            >
              <option value="exact">Exact path</option>
              <option value="prefix">Path and below</option>
            </select>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-[8rem_1fr_9rem]">
          <div>
            <label className="label" htmlFor="responseStatus">
              Status
            </label>
            <input
              id="responseStatus"
              name="responseStatus"
              type="number"
              min={100}
              max={599}
              className="input"
              defaultValue={route?.responseStatus ?? 200}
              required
            />
          </div>
          <div>
            <label className="label" htmlFor="responseType">
              Content-Type
            </label>
            <input
              id="responseType"
              name="responseType"
              className="input font-mono"
              list="content-types"
              defaultValue={route?.responseType ?? "application/json"}
              maxLength={255}
            />
            <datalist id="content-types">
              {CONTENT_TYPES.map((type) => (
                <option key={type} value={type} />
              ))}
            </datalist>
          </div>
          <div>
            <label className="label" htmlFor="delayMs">
              Delay (ms)
            </label>
            <input
              id="delayMs"
              name="delayMs"
              type="number"
              min={0}
              max={10000}
              step={100}
              className="input"
              defaultValue={route?.delayMs ?? 0}
            />
          </div>
        </div>
        <div>
          <label className="label" htmlFor="responseContent">
            Response body
          </label>
          <textarea
            id="responseContent"
            name="responseContent"
            className="input h-40 font-mono text-xs"
            placeholder='{"ok": true}'
            defaultValue={route?.responseContent ?? ""}
            spellCheck={false}
          />
        </div>
        <div>
          <label className="label" htmlFor="responseHeaders">
            Extra response headers (JSON)
          </label>
          <textarea
            id="responseHeaders"
            name="responseHeaders"
            className="input h-20 font-mono text-xs"
            placeholder='{"X-Request-Id": "abc"}'
            defaultValue={
              route && Object.keys(route.responseHeaders).length
                ? JSON.stringify(route.responseHeaders, null, 2)
                : ""
            }
            spellCheck={false}
          />
        </div>
        <div>
          <label className="label" htmlFor="description">
            Note
          </label>
          <input
            id="description"
            name="description"
            className="input"
            defaultValue={route?.description ?? ""}
            maxLength={1000}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={save.isLoading}>
            {route ? "Save route" : "Add route"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
